/**
 * Permission catalogue and default roles. Roles are stored in the database and editable
 * by administrators; these defaults are only used by the initial seed.
 */
export const PERMISSIONS = {
  'dashboard.view': 'عرض لوحة القيادة والمساعد الذكي',
  'operations.view': 'عرض يوم المناسبة والتقويم والتوصيل',
  'logistics.update': 'تحديث حالة التوصيل',
  'customers.view': 'عرض العملاء',
  'customers.manage': 'إضافة وتعديل العملاء',
  'customers.delete': 'حذف العملاء',
  'pipeline.view': 'عرض مسار المبيعات',
  'pipeline.manage': 'إدارة الفرص',
  'orders.view': 'عرض الطلبات',
  'orders.create': 'إنشاء الطلبات',
  'orders.edit': 'تعديل الطلبات والحالات',
  'orders.delete': 'حذف الطلبات',
  'payments.manage': 'تسجيل وتعديل وحذف الدفعات',
  'products.view': 'عرض المنتجات',
  'products.manage': 'إدارة المنتجات والصواني',
  'pricing.manage': 'التسعيرات',
  'pos.use': 'نقطة البيع',
  'commission.view': 'العمولة والإقفال',
  'finance.view': 'المالية والتحصيل',
  'accounting.view': 'المحاسبة والتقارير الضريبية',
  'accounting.manage': 'القيود اليدوية وإعدادات المنشأة',
  'suppliers.view': 'عرض الموردين والمشتريات',
  'suppliers.manage': 'إدارة الموردين والمشتريات والمدفوعات',
  'inventory.view': 'عرض المخزون',
  'inventory.manage': 'الجرد والإرجاع والتعديل والفاقد',
  'team.view': 'عرض الفريق',
  'team.manage': 'إدارة الفريق',
  'attendance.view': 'عرض الدوام',
  'attendance.manage': 'تسجيل الدوام',
  'reports.view': 'التقارير',
  'appointments.manage': 'المواعيد والخطط الموسمية والجودة',
  'settings.manage': 'الإعدادات العامة والتارغت',
  'users.manage': 'المستخدمون والصلاحيات',
  'audit.view': 'سجل التدقيق',
  'migration.run': 'ترحيل البيانات من النسخة القديمة',
  'data.reset': 'مسح كل البيانات',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

const view: Permission[] = [
  'dashboard.view',
  'operations.view',
  'customers.view',
  'pipeline.view',
  'orders.view',
  'products.view',
  'commission.view',
  'finance.view',
  'accounting.view',
  'suppliers.view',
  'inventory.view',
  'team.view',
  'attendance.view',
  'reports.view',
];

export interface RoleDef {
  key: string;
  name: string;
  description: string;
  permissions: Permission[];
}

export const DEFAULT_ROLES: RoleDef[] = [
  { key: 'admin', name: 'مدير النظام', description: 'كل الصلاحيات', permissions: ALL_PERMISSIONS },
  {
    key: 'manager',
    name: 'مدير',
    description: 'إدارة كاملة للعمليات والمالية بدون إدارة المستخدمين',
    permissions: ALL_PERMISSIONS.filter((p) => !['users.manage', 'data.reset', 'migration.run'].includes(p)),
  },
  {
    key: 'accountant',
    name: 'محاسب',
    description: 'المالية والمحاسبة والموردين',
    permissions: [...view, 'payments.manage', 'accounting.manage', 'suppliers.manage'],
  },
  {
    key: 'sales',
    name: 'مبيعات',
    description: 'العملاء والطلبات والتسعير',
    permissions: [
      'dashboard.view',
      'operations.view',
      'customers.view',
      'customers.manage',
      'pipeline.view',
      'pipeline.manage',
      'orders.view',
      'orders.create',
      'orders.edit',
      'payments.manage',
      'products.view',
      'pricing.manage',
      'commission.view',
      'reports.view',
      'appointments.manage',
    ],
  },
  {
    key: 'cashier',
    name: 'كاشير',
    description: 'نقطة البيع والتحصيل',
    permissions: ['dashboard.view', 'pos.use', 'products.view', 'orders.view', 'payments.manage', 'customers.view'],
  },
  {
    key: 'warehouse',
    name: 'مخزون وتوصيل',
    description: 'المخزون والتوصيل والمشتريات',
    permissions: [
      'dashboard.view',
      'operations.view',
      'logistics.update',
      'orders.view',
      'products.view',
      'products.manage',
      'inventory.view',
      'inventory.manage',
      'suppliers.view',
      'suppliers.manage',
      'reports.view',
    ],
  },
  { key: 'viewer', name: 'مشاهد', description: 'قراءة فقط', permissions: view },
];

/** Permission required to open each navigation page. */
export const PAGE_PERMISSIONS: Record<string, Permission> = {
  dashboard: 'dashboard.view',
  ai: 'dashboard.view',
  dayBoard: 'operations.view',
  calendar: 'operations.view',
  pipeline: 'pipeline.view',
  customers: 'customers.view',
  orders: 'orders.view',
  logistics: 'operations.view',
  products: 'products.view',
  pricing: 'pricing.manage',
  pos: 'pos.use',
  commission: 'commission.view',
  finance: 'finance.view',
  accounting: 'accounting.view',
  suppliers: 'suppliers.view',
  inventory: 'inventory.view',
  team: 'team.view',
  attendance: 'attendance.view',
  reports: 'reports.view',
  statistics: 'reports.view',
  appointments: 'appointments.manage',
  seasonal: 'appointments.manage',
  quality: 'appointments.manage',
};
